/**
 * x-image-compare directive.
 *
 * Provides an accessible before/after image comparison slider. The user can
 * drag or touch the slider handle to reveal more of either image. The handle
 * position is stored as a 0–100 percentage and mapped to a visual range that
 * keeps the handle away from the container edges.
 *
 * Sub-directives:
 *   x-image-compare:handle - Applied to the drag handle element. Adds ARIA
 *                            attributes and keyboard navigation support.
 *
 * @options (root only)
 *   min     - Minimum visual padding as a percentage (default: 10). The handle
 *             stays within the range `[min, 100-min]`.
 *   initial - Initial handle position as a 0–100 percentage (default: 50).
 *
 * @example
 *   <div x-image-compare="{ initial: 50, min: 10 }">
 *     <div x-image-compare:handle role="slider" tabindex="0">…</div>
 *   </div>
 */
(() => {
  const root = (Alpine, el, options = {}) => {
    Alpine.bind(el, () => ({
      ["x-data"]() {
        /** Visual padding: handle stays within [min, 100-min] range. */
        const min = options.min ?? 10;
        const range = 100 - min * 2;

        /** Bound handlers set in init() where Alpine's `this` is available. */
        let onMouseMove, onMouseUp, onTouchMove, onTouchEnd;

        return {
          isDragging: false,
          position: options.initial ?? 50,

          /**
           * Binds document-level drag handlers and sets the initial position.
           */
          init() {
            onMouseMove = (e) => {
              e.preventDefault();
              this.updatePosition(e);
            };
            onMouseUp = () => this.endDrag();
            onTouchMove = (e) => {
              e.preventDefault();
              this.updatePosition(e);
            };
            onTouchEnd = () => this.endDrag();

            this.setPosition(this.position);
          },

          /**
           * Cleans up any active drag on teardown.
           */
          destroy() {
            this.endDrag();
          },

          /**
           * Begins a drag interaction, capturing document-level pointer events.
           * @param {MouseEvent|TouchEvent} event - The initiating pointer event.
           */
          startDrag(event) {
            this.isDragging = true;
            this.updatePosition(event);

            document.addEventListener("mousemove", onMouseMove);
            document.addEventListener("mouseup", onMouseUp);
            document.addEventListener("touchmove", onTouchMove);
            document.addEventListener("touchend", onTouchEnd);
          },

          /**
           * Ends the active drag and removes document-level pointer listeners.
           */
          endDrag() {
            if (!this.isDragging) return;
            this.isDragging = false;

            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
            document.removeEventListener("touchmove", onTouchMove);
            document.removeEventListener("touchend", onTouchEnd);
          },

          /**
           * Calculates the new position from a pointer event and applies it.
           * Maps the raw pixel offset into the 0–100 data range, accounting
           * for the visual padding defined by `min`.
           * @param {MouseEvent|TouchEvent} event - The pointer event.
           */
          updatePosition(event) {
            const rect = this.$el.getBoundingClientRect();

            const clientX = event.touches
              ? event.touches[0].clientX
              : event.clientX;

            const x = clientX - rect.left;
            const rawPercent = (x / rect.width) * 100;

            /** Map from visual range back to 0–100 data range. */
            const percentage = Math.min(
              Math.max(((rawPercent - min) / range) * 100, 0),
              100,
            );

            this.setPosition(percentage);
          },

          /**
           * Applies the given position percentage and updates the CSS custom
           * property used to clip the overlay image. The data value (0–100) is
           * mapped to the visual range to keep the handle within the padded area.
           * E.g. min=10: position=0 → 10%, position=50 → 50%, position=100 → 90%.
           * @param {number} percentage - Position as a 0–100 value.
           */
          setPosition(percentage) {
            this.position = percentage;

            const visual = min + (percentage / 100) * range;

            this.$root.style.setProperty(
              "--image-compare-position",
              `${visual}%`,
            );
          },
        };
      },

      ["@mousedown"](e) {
        this.startDrag(e);
      },

      ["@touchstart.passive"](e) {
        this.startDrag(e);
      },
    }));
  };

  /**
   * Binds ARIA attributes and keyboard navigation to the slider handle element.
   * Supports Arrow keys (±5%), Shift+Arrow keys (±10%), Home (0%), and End (100%).
   *
   * @param {object} Alpine - The Alpine.js instance.
   * @param {HTMLElement} el - The handle element.
   */
  const handle = (Alpine, el) => {
    Alpine.bind(el, () => ({
      [":aria-valuenow"]() {
        return Math.round(this.position);
      },

      [":aria-valuetext"]() {
        const label =
          this.$el
            .closest("[x-image-compare]")
            ?.querySelector(".image-compare-label")
            ?.textContent?.trim() ?? "left";
        return `${Math.round(this.position)}% ${label}`;
      },

      ["@keydown"](event) {
        const step = event.shiftKey ? 10 : 5;
        let newPosition = this.position;

        switch (event.key) {
          case "ArrowLeft":
          case "ArrowDown":
            newPosition = Math.max(0, this.position - step);
            break;
          case "ArrowRight":
          case "ArrowUp":
            newPosition = Math.min(100, this.position + step);
            break;
          case "Home":
            newPosition = 0;
            break;
          case "End":
            newPosition = 100;
            break;
          default:
            return;
        }

        event.preventDefault();

        this.setPosition(newPosition);
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive(
      "image-compare",
      (el, { value, expression }, { evaluate }) => {
        const options = expression ? evaluate(expression) : {};

        if ("handle" === value) {
          handle(Alpine, el, options);
        } else {
          root(Alpine, el, options);
        }
      },
    );
  });
})();
